/**
 * Ship maintenance regime: bunkering time, annual class surveys, random port
 * state control inspections and mandatory dry docking. Day-to-day wear is
 * OperatingCostSystem's (reduced by engineers); this is the regulatory layer
 * on top. Logic lives in src/systems/MaintenanceSystem.js and the pure
 * helpers in src/utils/maintenance.js.
 */
export const MAINTENANCE = {
  // Bunkering (refuelling) — fuel pumped in tonnes per game hour by port
  // size, plus hose-up time. Runs in parallel with cargo handling.
  BUNKER_RATES: { tiny: 40, small: 80, medium: 200, large: 400, major: 800 },
  BUNKER_SETUP_HOURS: 0.5,

  // Repairs — also run in parallel with cargo handling and bunkering.
  // Hours = setup + points × hours-per-point (by port size) × ship-size factor
  // × (1 + session points / REPAIR_SCALE_POINTS): big jobs take
  // disproportionately long, so topping up a little at each stop while
  // loading beats one long lay-up. The superlinear part is measured over the
  // whole repair session, so back-to-back small repairs don't dodge it.
  REPAIR_HOURS_PER_POINT: { tiny: 0.5, small: 0.4, medium: 0.3, large: 0.25, major: 0.2 },
  REPAIR_SETUP_HOURS: 1,
  REPAIR_REFERENCE_TEU: 500,       // ship-size factor = sqrt(TEU / this), min REPAIR_MIN_SIZE_FACTOR
  REPAIR_MIN_SIZE_FACTOR: 0.3,
  REPAIR_SCALE_POINTS: 100,

  // Annual class survey — carried out automatically on docking at any port
  // with repair services once due. Ships below the minimum are held until
  // repaired to it.
  SURVEY_INTERVAL_DAYS: 365,
  SURVEY_MIN_CONDITION: 50,
  SURVEY_FEE_PCT: 0.0005,          // of ship base price
  SURVEY_GRACE_DAYS: 30,           // overdue fines start after this
  SURVEY_OVERDUE_FINE_PCT: 0.0001, // of base price, per day

  // Random port state control inspections — only at ports with repair
  // services. Chance per docking, by port size.
  INSPECTION_CHANCE: { tiny: 0.02, small: 0.03, medium: 0.04, large: 0.06, major: 0.08 },
  INSPECTION_POOR_CONDITION: 50,   // below this, inspection chance doubles
  INSPECTION_OVERDUE_MULT: 3,      // survey or dry dock overdue: inspectors target you
  DETENTION_CONDITION: 40,         // inspected below this → detained…
  DETENTION_RELEASE_CONDITION: 50, // …until repaired to this
  DETENTION_FINE_PCT: 0.001,       // of base price

  // Mandatory dry docking — at shipyard ports only. Interval by ship size
  // (small ships work harder and age faster).
  DRYDOCK_INTERVAL_YEARS: [
    { maxTeu: 100,      years: 2 },
    { maxTeu: 2000,     years: 3 },
    { maxTeu: 8000,     years: 4 },
    { maxTeu: Infinity, years: 5 }
  ],
  DRYDOCK_COST_PCT: 0.02,          // of base price; includes restoring condition to 100%
  DRYDOCK_BASE_DAYS: 3,
  DRYDOCK_DAYS_PER_1000_TEU: 0.5,
  DRYDOCK_WARNING_DAYS: [90, 30],
  DRYDOCK_OVERDUE_FINE_PCT: 0.001,        // of base price, per day
  DRYDOCK_OVERDUE_ESCALATION_DAYS: 90,    // fine doubles after this long overdue
  DRYDOCK_OVERDUE_ESCALATION_MULT: 2
};
